/**
 * The three places the dial pad looks for a number.
 *
 * One hook rather than three, because they share the search field and only one
 * of them is on screen at a time - and because the pad above the column needs
 * two of them for its own reason: the line under the number field names whoever
 * is being typed, and it can only do that from lists somebody already fetched.
 *
 * WHY EACH ONE FETCHES WHEN IT DOES:
 *
 *   - Recent calls load once, on mount, and are filtered in the browser. Twelve
 *     rows is the whole list, and it is the list the operator is most likely to
 *     want before they have typed anything at all.
 *   - The client book is searched, never listed. A CRM with two thousand
 *     clients has no useful "all contacts" view inside a 320px column.
 *   - The business register is searched and REFUSES to run on a short string.
 *     It is a mirror of 376 000 companies, and its own query planner needs
 *     three characters to reach the trigram index - below that the same search
 *     is a sequential scan over the whole table (see the measurements in
 *     `apps/api/src/modules/ariregister/lib/audience/queryMatch.ts`). So the
 *     floor here is not politeness, it is the difference between 0.2ms and
 *     200ms per keystroke.
 */
import { useEffect, useMemo, useState } from "react";
import { getClients } from "../../../lib/clients";
import { listCalls, type CallRow } from "../../../lib/calls";
import { listEntities } from "../../../lib/ariregister";
// Both mappings live in `candidate.ts`: React-free, so the rules about which
// rows are dropped and which identity travels with a re-dial have a test.
import { digitsOf, recentCandidates, registerCandidates, type Candidate } from "./candidate";

export type DialTab = "recent" | "contacts" | "register";

/** Below this the register's search cannot use its index. See the note above. */
export const REGISTER_MIN_QUERY = 3;

/** How many rows each searched source brings back. One screenful of column. */
const PAGE_SIZE = 20;

/** The client book is local and quick; the register is neither. */
const CONTACTS_DEBOUNCE_MS = 220;
const REGISTER_DEBOUNCE_MS = 320;

export interface DialLists {
  tab: DialTab;
  setTab: (tab: DialTab) => void;
  find: string;
  setFind: (find: string) => void;
  /** What the open tab is showing. */
  rows: Candidate[];
  /** Recent calls, loaded whichever tab is open - the match line reads them. */
  recent: Candidate[];
  /** Clients matching the search term, for the same reason. */
  contacts: Candidate[];
  /** A search is in flight and the list below is the previous answer. */
  busy: boolean;
  /** The open tab needs more typing before it will look anything up. */
  needsQuery: boolean;
}

export function useDialLists(): DialLists {
  const [tab, setTab] = useState<DialTab>("recent");
  const [find, setFind] = useState("");
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [contacts, setContacts] = useState<Candidate[]>([]);
  const [register, setRegister] = useState<Candidate[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listCalls({ pageSize: 12 })
      .then((r) => setCalls(r.rows))
      .catch(() => {
        // A pad that cannot reach the call log still dials. The column says
        // "nothing matched", which is true of a list nobody could load.
      });
  }, []);

  useEffect(() => {
    if (tab !== "contacts") return;
    const term = find.trim();
    let alive = true;
    const timer = setTimeout(() => {
      getClients({ q: term || undefined, pageSize: PAGE_SIZE })
        .then((r) => {
          if (!alive) return;
          setContacts(
            r.rows
              .filter((c) => c.phone)
              .map((c) => ({
                key: `client:${c.id}`,
                name: c.name,
                // A client always has a name, so this one has no fallback to
                // guard against. Written anyway, because `name` is what the
                // column draws and `companyName` is what the call is filed
                // under, and a source that fills only the first is the bug the
                // recent list had.
                companyName: c.name,
                detail: c.email ?? "",
                number: c.phone!,
              })),
          );
        })
        .catch(() => {});
    }, CONTACTS_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [tab, find]);

  useEffect(() => {
    if (tab !== "register") return;
    const term = find.trim();
    if (term.length < REGISTER_MIN_QUERY) {
      setRegister([]);
      setBusy(false);
      return;
    }
    let alive = true;
    setBusy(true);
    const timer = setTimeout(() => {
      listEntities({ q: term, hasPhone: "true", pageSize: PAGE_SIZE })
        .then((r) => {
          if (!alive) return;
          // The mapping, including which rows are dropped and the brain badge
          // that rides along, lives in `candidate.ts` so it can be tested
          // without React (AGENTS.md section 4).
          setRegister(registerCandidates(r.rows));
        })
        .catch(() => {
          // A permission taken away since the pad opened, or the register being
          // resynced. An empty list and the tab's own empty line say enough.
          if (alive) setRegister([]);
        })
        .finally(() => {
          if (alive) setBusy(false);
        });
    }, REGISTER_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [tab, find]);

  const recent = useMemo(() => recentCandidates(calls), [calls]);

  const rows = useMemo<Candidate[]>(() => {
    if (tab === "contacts") return contacts;
    if (tab === "register") return register;
    // The recent list is the only one filtered here rather than on the server:
    // it is twelve rows that are already in the browser.
    const term = find.trim().toLowerCase();
    if (!term) return recent;
    const termDigits = term.replace(/\D/g, "");
    return recent.filter((c) => {
      if (termDigits && digitsOf(c.number).includes(termDigits)) return true;
      return c.name.toLowerCase().includes(term);
    });
  }, [tab, contacts, register, recent, find]);

  return {
    tab,
    setTab,
    find,
    setFind,
    rows,
    recent,
    contacts,
    busy: tab === "register" && busy,
    needsQuery: tab === "register" && find.trim().length < REGISTER_MIN_QUERY,
  };
}
